import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import ServiceForm from 'components/Forms/ServiceForm'
import TextInput from 'components/Inputs/TextInput'
import Select from 'components/Inputs/Select'
import businessServiceValidator from 'validators/businessServiceValidator'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { ReactComponent as ButtonLoading } from 'icons/loading.svg'
import { Formik } from 'formik'

import {
  Container,
  Content,
  Row,
  InfoText,
  Button
} from '../styles'

export default function ProfessionalServicesModal ({
  visible,
  professionalID = null,
  serviceCategories = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [services, setServices] = useState ([])
  const [categories, setCategories] = useState ([])
  const [category, setCategory] = useState (null)
  const [name, setName] = useState ('')
  const [orderBy, setOrderBy] = useState ('')
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  useEffect (() => {
    getData ()
  }, [name, category, serviceCategories])

  const getData = async () => {
    try {
      setLoading (true)

      setCategories (serviceCategories.map (category => ({
        label: category.name,
        value: category.id
      })))

      await getServices ()
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const getServices = async () => {
    let filters = '?status=active&cutilei_services=false' + (
      professionalID ? `&professional_id=${professionalID}&new_services=false` : ''
    )

    if (name) filters += `&name=${encodeURIComponent (name)}`
    if (category) filters += `&category_id=${category.value}`
    if (orderBy) filters += `&order_by=${orderBy}`

    try {
      setLoading (true)

      const { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/services${filters}`,
        requestConfig
      )

      setServices (services)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  const getUniqueCategories = services => {
    const uniqueCategories = new Set ()

    services.forEach (service => {
      uniqueCategories.add (JSON.stringify (service.category))
    })

    const categories = Array.from (uniqueCategories).map (category => JSON.parse (category))
    return categories
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='xl' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Adicionar serviços</DialogTitle>
        </DialogHeader>
        {/* Os filtros ficam parados entre o título e a lista, que rola */}
        <Row style={{marginBottom: 15, marginInline: 20}}>
          <Select
            name='category'
            placeholder='Categoria...'
            value={category}
            options={categories}
            isClearable={true}
            onChange={(_, option) => setCategory (option)}
            containerStyles={{
              width: 280
            }}
          />
          <TextInput
            value={name}
            placeholder='Pesquisar por nome'
            isClearable={true}
            onChange={e => setName (e.target.value)}
            onClear={() => setName ('')}
            containerStyle={{width: 280}}
            inputStyle={{boxShadow: '0px 1px 10px -6px', paddingInline: 12}}
          />
        </Row>
        {loading ? (
          <DialogBody><Loading/></DialogBody>
        ) : (
          <Formik
            validationSchema={businessServiceValidator}
            initialValues={{
              'services': services.map (service => ({
                'id': service.id,
                'selected': false,
                'name': service.name,
                'nickname': service.nickname,
                'category': service.category,
                'duration': service.duration,
                'max_delay': service.max_delay,
                'price': service.price,
                'comission_percentage': service.comission_percentage * 100,
                'cutilei_service': service.cutilei_service,
                'accepts_online': service.accepts_online
              }))
            }}
            onSubmit={async (values, { setSubmitting }) => {
              try {
                const selectedServices = values.services
                  .filter (service => service.selected)
                  .map (service => ({
                    ...service, comission_percentage: service.comission_percentage / 100
                  }))
                const serviceCategories = getUniqueCategories (selectedServices)

                if (professionalID) {
                  await cutileiApi.post (
                    `/businesses/${businessId}/professionals/${professionalID}/services`,
                    {services: selectedServices},
                    requestConfig
                  )
                }

                setSubmitting (false)
                handleConfirm (selectedServices, serviceCategories)
                handleClose ()
              } catch (error) {
                setSubmitting (false)
                logApiError (error)
              }
            }}
          >
            {(formikProps) => (
              <>
                <DialogBody>
                  <Container>
                    <Content>
                      {services.length === 0 ? (
                        <InfoText>
                          Nenhum resultado encontrado
                        </InfoText>
                      ) : services.map ((service, index) => 
                        <ServiceForm 
                          key={service.id}
                          index={index}
                          showProductConfig={false}
                          formikProps={formikProps}
                          data={service}
                        />
                      )}
                    </Content>
                  </Container>
                </DialogBody>
                <DialogFooter>
                  <Row>
                    <Button type='submit' onClick={formikProps.handleSubmit}>
                      {formikProps.isSubmitting ? <ButtonLoading/> : 'Confirmar'}
                    </Button>
                  </Row>
                </DialogFooter>
              </>
            )}
          </Formik>
        )}
      </DialogContent>
    </Dialog>
  )
}
